/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";

import DataTable from "dashboard-template/examples/Tables/DataTable";
import { TableProps } from "interfaces";
import {
  AppointmentInterface,
  HeaderEnum,
  VisitOfficeStatusEnum,
  VisitStatusEnum,
  VisitTypeEnum,
} from "@malmur-technologies/medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectHasPermission } from "store/slices/auth.slice";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import CustomSelect from "components/generals/CustomSelect";
import { getSelectOptions } from "utils/form.utils";
import { CircularProgress, TextField } from "@mui/material";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { SelectArrayType } from "types/form.enum";
import { useNavigate } from "react-router-dom";
import Box from "components/generals/Box";
import { RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";

/**
 * Office visit table component for managing appointment visit types and statuses.
 * Provides inline editing of visit attributes directly in the table.
 * 
 * Features:
 * - Displays appointment information in sortable data table
 * - Inline editing of visit type (dropdown)
 * - Inline editing of office visit status (dropdown)
 * - Navigate to progress note for appointment
 * - Loading indicator during save operations
 * - Permission-based access control
 * - Auto-updates list after successful save
 * 
 * Visit Types:
 * - In-Person
 * - Telemedicine
 * - Phone Consultation
 * - Home Visit
 * - Emergency
 * 
 * Office Visit Statuses:
 * - Scheduled
 * - Checked In
 * - In Progress
 * - Completed
 * - Cancelled
 * - No Show
 * 
 * Inline Editing:
 * - Changes saved immediately on dropdown selection
 * - Loading spinner shown during save
 * - Success message on completion
 * - List automatically updated
 * 
 * Navigation:
 * - "Progress Note" button navigates to progress note page
 * - Passes appointmentId via route parameter
 * 
 * @param {TableProps<AppointmentInterface>} props - Table properties
 * @param {AppointmentInterface[]} props.modelList - Array of appointment records
 * @param {Function} props.setModelList - Callback to update appointment list
 * 
 * @returns {JSX.Element} Office visit table with inline editing
 */
const OfficeVisitTable: FC<TableProps<AppointmentInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [appointmentId, setAppointmentId] = useState<string>("");
  const hasEditPermission = useSelector(selectHasPermission(PermissionsUser[EntityPermissions.OFFICE_VISIT].EDIT));
  const generalSettings = useSelector(selectGeneralSettings);
  const isSimplifiedMode = generalSettings?.isSystemSimplified || false;
  const visitTypeListOptions = useMemo<SelectArrayType[]>(
    () => getSelectOptions(VisitTypeEnum, t),
    []
  );

  const visitOfficeStatus = useMemo<SelectArrayType[]>(
    () => getSelectOptions(VisitOfficeStatusEnum, t),
    []
  );

  // Helper function to get enum key from value
  const getEnumKeyFromValue = (enumObj: any, value: string): string => {
    const entry = Object.entries(enumObj).find(([key, val]) => val === value);
    return entry ? entry[0] : value;
  };

  // Helper to check if the appointment has a registered progressNote
  const isProgressNoteClosed = (appointment: AppointmentInterface) => {
    return Boolean(appointment.progressNote);
  };

  // Helper to check if the appointment edit window has passed (arrivalDateTime + duration)
  const isAppointmentEditWindowPassed = (appointment: AppointmentInterface) => {
    if (!appointment.arrivalDateTime || !appointment.duration) {
      return false;
    }
    const now = new Date();
    const arrivalTime = new Date(appointment.arrivalDateTime);
    const durationMinutes = appointment.duration || 0;
    const appointmentEndTime = new Date(arrivalTime.getTime() + durationMinutes * 60000);
    return appointmentEndTime < now;
  };

  // Helper to check if appointment is readonly (progressNote closed, status CANC/N/S/CO, or edit window passed)
  const isAppointmentReadonly = (appointment: AppointmentInterface) => {
    return (
      isProgressNoteClosed(appointment) ||
      appointment.visitStatus === VisitStatusEnum.CANCEL ||
      appointment.visitStatus === VisitStatusEnum.NO_SHOW ||
      appointment.visitStatus === VisitStatusEnum.CHECK_OUT ||
      isAppointmentEditWindowPassed(appointment)
    );
  };
  const handleVisitTypeChange = (
    newValue: string,
    id: string,
    appointmentTemplate: AppointmentInterface
  ) => {
    // Do not allow changes if there is a closed progressNote or edit window passed
    if (isProgressNoteClosed(appointmentTemplate) || isAppointmentEditWindowPassed(appointmentTemplate)) {
      return;
    }

    setAppointmentId(id);

    let appointment = appointmentTemplate;
    appointment.visitType = newValue as VisitTypeEnum;
    const data = JSON.stringify(appointment);

    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
      onSaveHandler,
      "PUT",
      data,
      [{ header: HeaderEnum.RESOURCE_ID, value: id }],
      { successMessage: t("officeVisitTable.updated") }
    );
  };

  const handleVisitOfficeStatusChange = (
    newValue: string,
    id: string,
    appointmentTemplate: AppointmentInterface
  ) => {
    // Do not allow changes if there is a closed progressNote or edit window passed
    if (isProgressNoteClosed(appointmentTemplate) || isAppointmentEditWindowPassed(appointmentTemplate)) {
      return;
    }

    let appointment = appointmentTemplate;
    setAppointmentId(id);
    appointment.visitOfficeStatus = newValue as VisitOfficeStatusEnum;

    const data = JSON.stringify(appointment);
    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
      onSaveHandler,
      "PUT",
      data,
      [{ header: HeaderEnum.RESOURCE_ID, value: id }],
      { successMessage: t("officeVisitTable.updated") }
    );
  };

  const onSaveHandler = (appointment: AppointmentInterface) => {
    sendRequest(CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS, null, "GET", null, [
      { header: HeaderEnum.RESOURCE_ID, value: appointment.id },
    ]).then((resp) => {
      if (resp.status === 200) {
        setModelList(modelList.map((model) => (model.id === appointment.id ? appointment : model)));
      }
      setAppointmentId("");
    });
  };

  const handleNumberChange = (
    newValue: string,
    id: string,
    appointmentTemplate: AppointmentInterface
  ) => {
    // Do not allow changes if there is a closed progressNote or edit window passed
    if (isProgressNoteClosed(appointmentTemplate) || isAppointmentEditWindowPassed(appointmentTemplate)) {
      return;
    }

    let appointment = appointmentTemplate;
    appointment.roomNumber = newValue;
    setAppointmentId(id);
    const data = JSON.stringify(appointment);
    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
      onSaveHandler,
      "PUT",
      data,
      [{ header: HeaderEnum.RESOURCE_ID, value: id }],
      { successMessage: t("officeVisitTable.updated") }
    );
  };

  const getUITable = (list: AppointmentInterface[]) => {
    return {
      columns: [
        {
          Header: t("officeVisitTable.patientName"),
          accessor: "patient",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <DefaultCell value={value?.person?.name + " " + value?.person?.lastname} />
          ),
        },
        {
          Header: t("officeVisitTable.doctorName"),
          accessor: "provider",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <DefaultCell value={value?.person?.name + " " + value?.person?.lastname} />
          ),
        },
        {
          Header: t("officeVisitTable.visitType"),
          accessor: "id",
          sortIndicatorType: "az",
          Cell: (props) => {
            if (isAppointmentReadonly(props.row.original) || !hasEditPermission || isSimplifiedMode) {
              const visitTypeKey = getEnumKeyFromValue(VisitTypeEnum, props?.row.original.visitType);
              return <DefaultCell value={t(`Enums.${visitTypeKey}`)} />;
            }
            return (
              <CustomSelect
                labelId="officevisit-visit-type"
                id="visittype-select"
                disabled={isLoading}
                value={props?.row.original.visitType}
                label={t("officeVisitTable.visitType")}
                selectArray={visitTypeListOptions}
                onChange={(evt) => {
                  const appointmentTemplate = list.find((p) => p.id === props.value);
                  if (appointmentTemplate) {
                    handleVisitTypeChange(
                      evt.target.value as string,
                      props.value,
                      appointmentTemplate
                    );
                  }
                }}
              />
            );
          },
        },
        {
          Header: t("officeVisitTable.visitStatus"),
          accessor: "visitStatus",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={t(`officeVisitTable.${value}`)} />,
        },
        {
          id: "visitOfficeStatus",
          Header: t("officeVisitTable.visitOfficeStatus"),
          accessor: "id",
          sortIndicatorType: "az",
          Cell: (props) => {
            if (isAppointmentReadonly(props.row.original) || !hasEditPermission || isSimplifiedMode) {
              const visitOfficeStatusKey = getEnumKeyFromValue(VisitOfficeStatusEnum, props?.row.original.visitOfficeStatus);
              return <DefaultCell value={t(`Enums.${visitOfficeStatusKey}`)} />;
            }
            return (
              <CustomSelect
                labelId="officevisit-visitOffice-status"
                id="visitOfficeStatus-select"
                disabled={isLoading}
                value={props?.row.original.visitOfficeStatus}
                label="Visit Office Status"
                selectArray={visitOfficeStatus}
                onChange={(evt) => {
                  const appointmentTemplate = list.find((p) => p.id === props.value);
                  if (appointmentTemplate) {
                    handleVisitOfficeStatusChange(
                      evt.target.value as string,
                      props.value,
                      appointmentTemplate
                    );
                  }
                }}
              />
            );
          },
        },
        {
          id: "roomNumber",
          Header: t("officeVisitTable.roomNumber"),
          sortIndicatorType: "numeric",
          accessor: "id",
          Cell: (props) => {
            if (isAppointmentReadonly(props.row.original) || !hasEditPermission) {
              return <DefaultCell value={props?.row.original.roomNumber} />;
            }
            return (
              <TextField
                defaultValue={props?.row.original.roomNumber}
                disabled={isLoading}
                InputProps={{ inputProps: { min: 0, max: 999, maxLength: 3 } }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.value = Math.max(0, parseInt(target.value))
                    .toString()
                    .slice(0, target.maxLength);
                }}
                onBlur={(event) => {
                  const appointmentTemplate = list.find((p) => p.id === props.value);
                  handleNumberChange(event.target.value, props.value, appointmentTemplate);
                }}
                id="outlined-basic"
                variant="outlined"
                type="number"
              />
            );
          },
        },
        {
          id: "viewAppointment",
          Header: t("officeVisitTable.progressNote"),
          disableSortBy: true,
          accessor: "id",
          Cell: ({ value }: any) => (
            <Box sx={{ display: "flex" }}>
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW]}
              >
                <Button
                  id="cy-view-progress-note"
                  variant="contained"
                  color="info"
                  disabled={isLoading}
                  onClick={() => {
                    handleNewProgressNote(value);
                  }}
                >
                  {t("patientTable.view")}
                </Button>
              </PermissionWrapper>
              {isLoading && value === appointmentId && <CircularProgress color="success" />}
            </Box>
          ),
        },
      ],
      rows: list,
    };
  };

  function handleNewProgressNote(data: string) {
    navigate(RoutesConstants.PROGRESS_NOTE, { state: { data } });
  }

  const dataTableData = useMemo(() => getUITable(modelList), [modelList]);

  return <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading} 
      onSortChange={props.onSortChange}
      currentSortBy={props.currentSortBy}
      currentSortOrder={props.currentSortOrder}
    />;
};

export default OfficeVisitTable;
